import * as React from "react";
import {
  Drawer,
  Autocomplete,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../assets/styles/tokens";

// Brand Name search shared by the Be Aware mobile tabs. Owns the trigger icon
// and the bottom sheet; the selected/typed value is controlled by the caller.
// Design mirrors Budget Control > Card View (Mobile).
interface BeAwareBrandSearchDrawerProps {
  brandList?: string[];
  brandName?: string;
  onBrandNameChange?: (value: string) => void;
  dataTracker?: string;
}

export default function BeAwareBrandSearchDrawer({
  brandList = [],
  brandName = "",
  onBrandNameChange,
  dataTracker,
}: BeAwareBrandSearchDrawerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      {/* Search — opens the brand-name bottom sheet */}
      <IconButton
        color="secondary"
        size="large"
        onClick={() => setOpen(true)}
        data-tracker={dataTracker}
      >
        <SearchIcon sx={{ color: colors.GrayLightMode[700].$value }} />
      </IconButton>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: spacing.Radius.lg.$value,
              borderTopRightRadius: spacing.Radius.lg.$value,
              p: spacing.Spacing.lg.$value,
            },
          },
        }}
      >
        {/* drag handle */}
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: colors.GrayLightMode[300].$value,
            mx: "auto",
            mb: spacing.Spacing.lg.$value,
          }}
        />

        <Typography
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            color: colors.GrayLightMode[900].$value,
            mb: spacing.Spacing.md.$value,
          }}
        >
          Search Brand Name
        </Typography>

        <Autocomplete
          fullWidth
          freeSolo
          forcePopupIcon
          options={brandList}
          value={brandName || ""}
          disableClearable={!brandName}
          onInputChange={(_, newVal) => onBrandNameChange?.(newVal ?? "")}
          onChange={(_, newValue) => onBrandNameChange?.(newValue ?? "")}
          getOptionLabel={(opt) => opt || ""}
          slotProps={{
            popper: {
              placement: "top-start",
              modifiers: [{ name: "flip", enabled: false }],
            },
            listbox: { sx: { maxHeight: 240 } },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Brand Name"
              color="secondary"
              size="small"
              inputProps={{
                ...params.inputProps,
                type: "search",
                autoComplete: "off",
              }}
              sx={{
                '& input[type="search"]::-webkit-search-cancel-button': {
                  display: "none",
                  WebkitAppearance: "none",
                },
                '& input[type="search"]::-webkit-search-decoration': {
                  WebkitAppearance: "none",
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Drawer>
    </>
  );
}
